import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { FaEdit, FaTrash, FaEye, FaBox, FaTools, FaClock, FaMapMarkerAlt, FaUser, FaSearch, FaFilter, FaCheck, FaTimes, FaPhone, FaEnvelope, FaWhatsapp, FaBuilding, FaStickyNote, FaHistory, FaCopy } from 'react-icons/fa';
import AdminLayout from '../../components/AdminLayout';

export default function RequestsManagement() {
  const { i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const [requests, setRequests] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  
  // Edit dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [editForm, setEditForm] = useState({
    title_fr: '',
    title_en: '',
    description_fr: '',
    description_en: '',
    request_type: 'product',
    category_id: '',
    quantity: '',
    unit: '',
    deadline: '',
    budget: '',
    location: '',
    status: 'active',
    position: 999
  });

  // View dialog
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewRequest, setViewRequest] = useState(null);
  const [requesterInfo, setRequesterInfo] = useState(null);
  const [newNote, setNewNote] = useState('');
  const [loadingRequester, setLoadingRequester] = useState(false);

  useEffect(() => {
    if (user) {
      fetchRequests();
      fetchCategories();
    }
  }, [user]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/requests/admin/all?admin_id=${user.id}`);
      setRequests(response.data);
    } catch (error) {
      console.error('Error fetching requests:', error);
      // Fallback to public endpoint
      try {
        const fallbackResponse = await axios.get(`${API}/requests/public/latest?limit=100`);
        setRequests(fallbackResponse.data);
      } catch (e) {
        toast.error('Erreur lors du chargement des demandes');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleDelete = async (requestId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette demande ?')) {
      return;
    }

    try {
      await axios.delete(`${API}/requests/delete/${requestId}?user_id=${user.id}`);
      toast.success('Demande supprimée avec succès');
      fetchRequests();
    } catch (error) {
      console.error('Error deleting request:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleEdit = (request) => {
    setSelectedRequest(request);
    setEditForm({
      title_fr: request.title_fr || '',
      title_en: request.title_en || '',
      description_fr: request.description_fr || '',
      description_en: request.description_en || '',
      request_type: request.request_type || 'product',
      category_id: request.category_id || '',
      quantity: request.quantity || '',
      unit: request.unit || '',
      deadline: request.deadline || '',
      budget: request.budget || '',
      location: request.location || '',
      status: request.status || 'active',
      position: request.position ?? 999
    });
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedRequest) return;

    try {
      // Update request data
      await axios.put(
        `${API}/requests/update/${selectedRequest.id}?user_id=${user.id}`,
        {
          ...editForm,
          quantity: editForm.quantity ? parseInt(editForm.quantity) : null,
          budget: editForm.budget ? parseFloat(editForm.budget) : null,
          position: editForm.position
        }
      );
      
      // Also update position via dedicated endpoint if changed
      if (editForm.position !== selectedRequest.position) {
        await axios.patch(
          `${API}/requests/admin/${selectedRequest.id}/position?admin_id=${user.id}&position=${editForm.position}`
        );
      }
      
      toast.success('Demande mise à jour avec succès');
      setEditDialogOpen(false);
      fetchRequests();
    } catch (error) {
      console.error('Error updating request:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleView = async (request) => {
    setViewRequest(request);
    setViewDialogOpen(true);
    setRequesterInfo(null);
    setNewNote('');
    
    // Fetch requester info if user_id exists
    if (request.user_id) {
      setLoadingRequester(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/users/${request.user_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setRequesterInfo(response.data);
      } catch (error) {
        console.error('Error fetching requester info:', error);
        // Try to extract info from request itself
        setRequesterInfo({
          name: request.contact_name || request.user_name || 'Non renseigné',
          email: request.contact_email || request.user_email || '',
          phone: request.contact_phone || request.user_phone || '',
          company_name: request.company_name || ''
        });
      } finally {
        setLoadingRequester(false);
      }
    } else {
      // Use contact info from request
      setRequesterInfo({
        name: request.contact_name || 'Anonyme',
        email: request.contact_email || '',
        phone: request.contact_phone || '',
        company_name: request.company_name || ''
      });
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !viewRequest) return;
    
    try {
      const token = localStorage.getItem('token');
      const noteEntry = {
        text: newNote,
        author: user.name || user.email,
        date: new Date().toISOString()
      };
      
      const existingNotes = viewRequest.admin_notes || [];
      const updatedNotes = [...existingNotes, noteEntry];
      
      await axios.put(
        `${API}/requests/update/${viewRequest.id}?user_id=${user.id}`,
        { admin_notes: updatedNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setViewRequest({ ...viewRequest, admin_notes: updatedNotes });
      setNewNote('');
      toast.success('Note ajoutée');
      fetchRequests();
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error('Erreur lors de l\'ajout de la note');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié !');
  };

  const formatPhoneForWhatsApp = (phone) => {
    if (!phone) return '';
    // Remove spaces, dashes, dots and ensure it starts with country code
    let cleaned = phone.replace(/[\s\-\.]/g, '');
    if (cleaned.startsWith('0')) {
      cleaned = '33' + cleaned.substring(1); // Assuming France
    }
    if (!cleaned.startsWith('+')) {
      cleaned = '+' + cleaned;
    }
    return cleaned.replace('+', '');
  };

  const handleStatusChange = async (requestId, newStatus) => {
    try {
      await axios.put(
        `${API}/requests/update/${requestId}?user_id=${user.id}`,
        { status: newStatus }
      );
      toast.success(`Statut changé en "${newStatus}"`);
      fetchRequests();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Erreur lors du changement de statut');
    }
  };

  // Filter requests
  const filteredRequests = requests.filter(req => {
    const matchesSearch = 
      (req.title_fr?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (req.title_en?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (req.description_fr?.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = statusFilter === 'all' || req.status === statusFilter;
    const matchesType = typeFilter === 'all' || req.request_type === typeFilter;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-800">Actif</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800">En attente</Badge>;
      case 'closed':
        return <Badge className="bg-gray-100 text-gray-800">Fermé</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800">Rejeté</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getTypeBadge = (type) => {
    return type === 'product' 
      ? <Badge variant="outline" className="border-blue-500 text-blue-600"><FaBox className="mr-1" /> Produit</Badge>
      : <Badge variant="outline" className="border-purple-500 text-purple-600"><FaTools className="mr-1" /> Service</Badge>;
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#0A2540]">Gestion des Demandes</h1>
            <p className="text-gray-500 mt-1">{filteredRequests.length} demande(s) trouvée(s)</p>
          </div>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    placeholder="Rechercher une demande..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="active">Actif</SelectItem>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="closed">Fermé</SelectItem>
                  <SelectItem value="rejected">Rejeté</SelectItem>
                </SelectContent>
              </Select>

              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  <SelectItem value="product">Produit</SelectItem>
                  <SelectItem value="service">Service</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Requests Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#635BFF] mx-auto"></div>
                <p className="mt-4 text-gray-500">Chargement...</p>
              </div>
            ) : filteredRequests.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                Aucune demande trouvée
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left p-4 font-medium text-gray-600">Titre</th>
                      <th className="text-left p-4 font-medium text-gray-600">Demandeur</th>
                      <th className="text-left p-4 font-medium text-gray-600">Type</th>
                      <th className="text-left p-4 font-medium text-gray-600">Statut</th>
                      <th className="text-left p-4 font-medium text-gray-600">Lieu</th>
                      <th className="text-left p-4 font-medium text-gray-600">Date</th>
                      <th className="text-center p-4 font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredRequests.map(request => (
                      <tr key={request.id} className="hover:bg-gray-50">
                        <td className="p-4">
                          <div>
                            <p className="font-medium text-[#0A2540]">{request.title_fr || request.title_en}</p>
                            <p className="text-sm text-gray-500 truncate max-w-xs">
                              {request.description_fr?.substring(0, 60)}...
                            </p>
                          </div>
                        </td>
                        <td className="p-4">
                          <div className="text-sm">
                            <p className="font-medium text-[#0A2540]">
                              {request.contact_name || request.user_name || 'Anonyme'}
                            </p>
                            {(request.contact_phone || request.user_phone) && (
                              <p className="text-gray-500 flex items-center gap-1">
                                <FaPhone className="w-3 h-3" />
                                {request.contact_phone || request.user_phone}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="p-4">{getTypeBadge(request.request_type)}</td>
                        <td className="p-4">{getStatusBadge(request.status)}</td>
                        <td className="p-4">
                          {request.location && (
                            <span className="flex items-center text-sm text-gray-600">
                              <FaMapMarkerAlt className="mr-1 text-[#00D4FF]" />
                              {request.location}
                            </span>
                          )}
                        </td>
                        <td className="p-4 text-sm text-gray-500">
                          {new Date(request.created_at).toLocaleDateString('fr-FR')}
                        </td>
                        <td className="p-4">
                          <div className="flex items-center justify-center gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleView(request)}
                              title="Voir"
                            >
                              <FaEye className="text-gray-500" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleEdit(request)}
                              title="Modifier"
                            >
                              <FaEdit className="text-blue-500" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleDelete(request.id)}
                              title="Supprimer"
                            >
                              <FaTrash className="text-red-500" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* View Dialog - Enhanced */}
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Détails de la demande</DialogTitle>
            </DialogHeader>
            {viewRequest && (
              <div className="space-y-6">
                {/* Status & Type Badges */}
                <div className="flex items-center gap-3">
                  {getTypeBadge(viewRequest.request_type)}
                  {getStatusBadge(viewRequest.status)}
                  <span className="text-sm text-gray-500">
                    Créé le {new Date(viewRequest.created_at).toLocaleDateString('fr-FR')}
                  </span>
                </div>
                
                {/* Title */}
                <div>
                  <h3 className="text-xl font-bold text-[#0A2540]">{viewRequest.title_fr}</h3>
                  {viewRequest.title_en && viewRequest.title_en !== viewRequest.title_fr && (
                    <p className="text-gray-500">{viewRequest.title_en}</p>
                  )}
                </div>

                {/* DEMANDEUR SECTION - NEW */}
                <div className="bg-gradient-to-r from-[#635BFF]/10 to-[#00D4FF]/10 rounded-xl p-5 border border-[#635BFF]/20">
                  <h4 className="font-bold text-[#0A2540] mb-4 flex items-center gap-2">
                    <FaUser className="text-[#635BFF]" />
                    Informations du demandeur
                  </h4>
                  
                  {loadingRequester ? (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#635BFF] mx-auto"></div>
                    </div>
                  ) : requesterInfo ? (
                    <div className="space-y-3">
                      {/* Nom */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <FaUser className="text-gray-400 w-4" />
                          <span className="font-medium">{requesterInfo.name || 'Non renseigné'}</span>
                        </div>
                        {requesterInfo.name && (
                          <Button variant="ghost" size="sm" onClick={() => copyToClipboard(requesterInfo.name)}>
                            <FaCopy className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                      
                      {/* Entreprise */}
                      {requesterInfo.company_name && (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <FaBuilding className="text-gray-400 w-4" />
                            <span>{requesterInfo.company_name}</span>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => copyToClipboard(requesterInfo.company_name)}>
                            <FaCopy className="w-3 h-3" />
                          </Button>
                        </div>
                      )}
                      
                      {/* Téléphone avec actions */}
                      {requesterInfo.phone && (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <FaPhone className="text-gray-400 w-4" />
                            <span>{requesterInfo.phone}</span>
                          </div>
                          <div className="flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-green-600 hover:bg-green-50"
                              onClick={() => window.open(`https://wa.me/${formatPhoneForWhatsApp(requesterInfo.phone)}`, '_blank')}
                              title="WhatsApp"
                            >
                              <FaWhatsapp className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-blue-600 hover:bg-blue-50"
                              onClick={() => window.open(`tel:${requesterInfo.phone}`, '_blank')}
                              title="Appeler"
                            >
                              <FaPhone className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => copyToClipboard(requesterInfo.phone)}>
                              <FaCopy className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      )}
                      
                      {/* Email avec actions */}
                      {requesterInfo.email && (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <FaEnvelope className="text-gray-400 w-4" />
                            <span className="text-sm">{requesterInfo.email}</span>
                          </div>
                          <div className="flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-blue-600 hover:bg-blue-50"
                              onClick={() => window.open(`mailto:${requesterInfo.email}?subject=Re: ${viewRequest.title_fr}`, '_blank')}
                              title="Envoyer email"
                            >
                              <FaEnvelope className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => copyToClipboard(requesterInfo.email)}>
                              <FaCopy className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      )}
                      
                      {/* Quick Actions */}
                      <div className="flex gap-2 pt-3 border-t mt-3">
                        {requesterInfo.phone && (
                          <>
                            <Button 
                              size="sm" 
                              className="bg-green-500 hover:bg-green-600 text-white"
                              onClick={() => window.open(`https://wa.me/${formatPhoneForWhatsApp(requesterInfo.phone)}?text=${encodeURIComponent(`Bonjour, je vous contacte concernant votre demande "${viewRequest.title_fr}" sur GEOTS.`)}`, '_blank')}
                            >
                              <FaWhatsapp className="w-4 h-4 mr-2" />
                              WhatsApp
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => window.open(`tel:${requesterInfo.phone}`)}
                            >
                              <FaPhone className="w-4 h-4 mr-2" />
                              Appeler
                            </Button>
                          </>
                        )}
                        {requesterInfo.email && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => window.open(`mailto:${requesterInfo.email}?subject=Re: ${viewRequest.title_fr}&body=Bonjour,%0A%0AJe vous contacte concernant votre demande sur GEOTS.consulting.%0A%0ACordialement`)}
                          >
                            <FaEnvelope className="w-4 h-4 mr-2" />
                            Email
                          </Button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500 italic">Aucune information disponible</p>
                  )}
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  {viewRequest.location && (
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-gray-500 text-xs mb-1">Localisation</p>
                      <p className="flex items-center gap-1 font-medium">
                        <FaMapMarkerAlt className="text-[#00D4FF]" />
                        {viewRequest.location}
                      </p>
                    </div>
                  )}
                  {viewRequest.deadline && (
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-gray-500 text-xs mb-1">Délai</p>
                      <p className="flex items-center gap-1 font-medium">
                        <FaClock className="text-orange-500" />
                        {viewRequest.deadline}
                      </p>
                    </div>
                  )}
                  {viewRequest.budget && (
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-gray-500 text-xs mb-1">Budget</p>
                      <p className="font-bold text-[#635BFF]">{viewRequest.budget}€</p>
                    </div>
                  )}
                  {viewRequest.quantity && (
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <p className="text-gray-500 text-xs mb-1">Quantité</p>
                      <p className="font-medium">{viewRequest.quantity} {viewRequest.unit}</p>
                    </div>
                  )}
                </div>

                {/* Description */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium mb-2">Description</h4>
                  <p className="text-gray-700 whitespace-pre-wrap">{viewRequest.description_fr}</p>
                </div>

                {/* Images */}
                {viewRequest.images && viewRequest.images.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Photos jointes</h4>
                    <div className="grid grid-cols-4 gap-2">
                      {viewRequest.images.map((img, idx) => (
                        <a key={idx} href={img} target="_blank" rel="noopener noreferrer">
                          <img src={img} alt="" className="w-full h-24 object-cover rounded hover:opacity-80" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                {/* NOTES SECTION - NEW */}
                <div className="bg-yellow-50 rounded-xl p-5 border border-yellow-200">
                  <h4 className="font-bold text-[#0A2540] mb-4 flex items-center gap-2">
                    <FaStickyNote className="text-yellow-600" />
                    Notes internes
                  </h4>
                  
                  {/* Existing Notes */}
                  {viewRequest.admin_notes && viewRequest.admin_notes.length > 0 ? (
                    <div className="space-y-3 mb-4">
                      {viewRequest.admin_notes.map((note, idx) => (
                        <div key={idx} className="bg-white p-3 rounded-lg border border-yellow-100">
                          <p className="text-gray-700">{note.text}</p>
                          <p className="text-xs text-gray-400 mt-2">
                            {note.author} • {new Date(note.date).toLocaleString('fr-FR')}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 italic mb-4">Aucune note pour le moment</p>
                  )}
                  
                  {/* Add Note Form */}
                  <div className="flex gap-2">
                    <Input
                      placeholder="Ajouter une note (ex: Appelé le..., Relance prévue...)"
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddNote()}
                      className="flex-1"
                    />
                    <Button onClick={handleAddNote} className="bg-yellow-500 hover:bg-yellow-600">
                      Ajouter
                    </Button>
                  </div>
                </div>

                {/* Status Change Quick Actions */}
                <div className="flex flex-wrap gap-2 pt-4 border-t">
                  <span className="text-sm text-gray-500 mr-2">Changer le statut :</span>
                  <Button 
                    size="sm" 
                    variant={viewRequest.status === 'approved' ? 'default' : 'outline'}
                    className={viewRequest.status === 'approved' ? 'bg-green-500' : ''}
                    onClick={() => { handleStatusChange(viewRequest.id, 'approved'); setViewRequest({...viewRequest, status: 'approved'}); }}
                  >
                    <FaCheck className="w-3 h-3 mr-1" /> Approuvé
                  </Button>
                  <Button 
                    size="sm" 
                    variant={viewRequest.status === 'pending' ? 'default' : 'outline'}
                    className={viewRequest.status === 'pending' ? 'bg-yellow-500' : ''}
                    onClick={() => { handleStatusChange(viewRequest.id, 'pending'); setViewRequest({...viewRequest, status: 'pending'}); }}
                  >
                    <FaClock className="w-3 h-3 mr-1" /> En attente
                  </Button>
                  <Button 
                    size="sm" 
                    variant={viewRequest.status === 'closed' ? 'default' : 'outline'}
                    className={viewRequest.status === 'closed' ? 'bg-gray-500' : ''}
                    onClick={() => { handleStatusChange(viewRequest.id, 'closed'); setViewRequest({...viewRequest, status: 'closed'}); }}
                  >
                    <FaTimes className="w-3 h-3 mr-1" /> Fermé
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Modifier la demande</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Titre (FR) *</Label>
                  <Input
                    value={editForm.title_fr}
                    onChange={(e) => setEditForm({...editForm, title_fr: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Title (EN)</Label>
                  <Input
                    value={editForm.title_en}
                    onChange={(e) => setEditForm({...editForm, title_en: e.target.value})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Description (FR) *</Label>
                  <Textarea
                    value={editForm.description_fr}
                    onChange={(e) => setEditForm({...editForm, description_fr: e.target.value})}
                    rows={4}
                  />
                </div>
                <div>
                  <Label>Description (EN)</Label>
                  <Textarea
                    value={editForm.description_en}
                    onChange={(e) => setEditForm({...editForm, description_en: e.target.value})}
                    rows={4}
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Type</Label>
                  <Select value={editForm.request_type} onValueChange={(v) => setEditForm({...editForm, request_type: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="product">Produit</SelectItem>
                      <SelectItem value="service">Service</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Statut</Label>
                  <Select value={editForm.status} onValueChange={(v) => setEditForm({...editForm, status: v})}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="approved">Approuvé (Visible publiquement)</SelectItem>
                      <SelectItem value="active">Actif</SelectItem>
                      <SelectItem value="pending">En attente</SelectItem>
                      <SelectItem value="closed">Fermé</SelectItem>
                      <SelectItem value="rejected">Rejeté</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Catégorie</Label>
                  <Select value={editForm.category_id} onValueChange={(v) => setEditForm({...editForm, category_id: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.name_fr}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Quantité</Label>
                  <Input
                    type="number"
                    value={editForm.quantity}
                    onChange={(e) => setEditForm({...editForm, quantity: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Unité</Label>
                  <Input
                    value={editForm.unit}
                    onChange={(e) => setEditForm({...editForm, unit: e.target.value})}
                    placeholder="ex: pcs, m³"
                  />
                </div>
                <div>
                  <Label>Budget (€)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editForm.budget}
                    onChange={(e) => setEditForm({...editForm, budget: e.target.value})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Délai</Label>
                  <Input
                    value={editForm.deadline}
                    onChange={(e) => setEditForm({...editForm, deadline: e.target.value})}
                    placeholder="ex: 2 semaines, urgent"
                  />
                </div>
                <div>
                  <Label>Localisation</Label>
                  <Input
                    value={editForm.location}
                    onChange={(e) => setEditForm({...editForm, location: e.target.value})}
                    placeholder="ex: Paris, France"
                  />
                </div>
              </div>

              {/* Position / Ranking */}
              <div className="flex items-center gap-4 p-3 bg-[#635BFF]/5 rounded-lg border border-[#635BFF]/20">
                <div className="flex-1">
                  <Label className="text-[#635BFF] font-medium">Position d'affichage</Label>
                  <p className="text-xs text-gray-500">Plus la valeur est basse, plus haut dans la liste (1 = premier)</p>
                </div>
                <Input
                  type="number"
                  min="1"
                  className="w-24"
                  value={editForm.position}
                  onChange={(e) => setEditForm({...editForm, position: parseInt(e.target.value) || 999})}
                />
              </div>
            </div>

            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleSaveEdit} className="bg-[#635BFF]">
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
